import * as THREE from 'three';
import { HollyRig } from '@/anim/hollyRig';
import { HOLLY_CLIPS } from '@/anim/hollyClips';
import { ease } from '@/anim/types';
import { blendHollyPose, neutralHollyPose } from '@/anim/hollyTypes';
import type { CompanionSnapshot } from '@/systems/companion';
import type { CompanionBellEvent } from '@/systems/companionBell';
import { HOLLY_BELL_HEIGHT } from '@/models/holly';
import { HOLLY_BELL_PLAYBACK_GAIN, HOLLY_BELL_SOUND, HOLLY_BELL_STRIDE_METRES } from '@/audio/sounds/bell';
import { MOVE_SPEED as HOLLY_MOVE_SPEED } from '@/story/act2/woolShedPuzzle';

export interface HollyClocks {
  readonly latchProgress: number;
  readonly doorProgress: number;
  readonly riseProgress: number;
}

/**
 * Metres of ground per walk cycle, DERIVED from the two numbers that decide it.
 *
 * Writing 1.21 here would be a third place her gait speed is described, and
 * `docs/traps.md` is explicit that a derived expected value comes from the
 * exported constants and never from arithmetic done twice in somebody's head.
 */
const HOLLY_WALK_STRIDE = HOLLY_MOVE_SPEED * HOLLY_CLIPS.walk.duration;

/** Presentation owns the audio engine; simulation hands it complete bell marks. */
export function drainHollyBellQueue(
  queue: CompanionBellEvent[],
  playOneShot: (id: string, options: { gain: number; position: { x: number; y: number; z: number }; detune?: number }) => void,
): void {
  for (const event of queue) {
    const detune = ((Math.floor(event.thresholdDistance / HOLLY_BELL_STRIDE_METRES) % 3) - 1) * 9;
    playOneShot(HOLLY_BELL_SOUND, { gain: HOLLY_BELL_PLAYBACK_GAIN, position: { x: event.position.x, y: HOLLY_BELL_HEIGHT, z: event.position.z }, detune });
  }
  queue.length = 0;
}

/** The one presentation path for Holly, shared by the shed and the night field. */
export class HollyPresenter {
  readonly #pose = neutralHollyPose();
  readonly #poseB = neutralHollyPose();
  #renderTime = 0;

  constructor(
    readonly root: THREE.Group,
    readonly rig: HollyRig,
  ) {}

  update(
    frameDt: number,
    holly: CompanionSnapshot,
    clocks: HollyClocks | null,
    groundY: ((x: number, z: number) => number) | null,
  ): void {
    this.#renderTime += frameDt;
    // Companion is flat; only presentation knows this room's drawn slope.
    this.root.position.set(holly.x, groundY?.(holly.x, holly.z) ?? 0, holly.z);
    this.root.rotation.y = holly.yaw;

    // Which clip, and why the phase comes from where it does in each case.
    if (holly.phase === 'trapped') {
      HOLLY_CLIPS.trapped.sample((this.#renderTime / HOLLY_CLIPS.trapped.duration) % 1, this.#pose);
    } else if (holly.phase === 'at-high-latch') {
      // **The reach's phase IS `latchProgress`.** The puzzle already produces
      // a 0..1 over exactly this interval; driving the clip from a second
      // clock is how the bar comes off in the picture at a different moment
      // from the one the simulation opened the door on.
      HOLLY_CLIPS.reach.sample(clocks?.latchProgress ?? 1, this.#pose);
    } else if (holly.phase === 'opening-door') {
      // **She comes down off the door on the leaf's own clock.** Until this
      // existed the branch fell through to `breath` and she snapped from a
      // full 23° rear to standing in a single frame, in the shot the whole
      // beat has been building to. It is not a new clock (D-157): the swing
      // is already publishing a 0..1 over exactly this interval, and the
      // bar, the leaf and her forefeet all leaving it together is what the
      // moment actually is.
      HOLLY_CLIPS.reach.sample(1, this.#poseB);
      HOLLY_CLIPS.breath.sample((this.#renderTime / HOLLY_CLIPS.breath.duration) % 1, this.#pose);
      blendHollyPose(this.#poseB, this.#pose, ease.inOut(clocks?.doorProgress ?? 1), this.#pose);
    } else if (holly.phase === 'resting') {
      // The rest state, and the only clip in her set that loops on wall time
      // rather than on a puzzle clock — because nothing is happening, which
      // is the point of the room.
      HOLLY_CLIPS.doze.sample((this.#renderTime / HOLLY_CLIPS.doze.duration) % 1, this.#pose);
    } else if (holly.phase === 'following' && holly.moving) {
      // Phase from DISTANCE TRAVELLED, not from time. A gait clocked in
      // seconds slides its feet the moment the walker is not at nominal
      // speed, and hers is a damped follow that almost never is.
      HOLLY_CLIPS.walk.sample((holly.travelledDistance / HOLLY_WALK_STRIDE) % 1, this.#pose);
    } else {
      HOLLY_CLIPS.breath.sample((this.#renderTime / HOLLY_CLIPS.breath.duration) % 1, this.#pose);
    }

    // Coming out of the wire she stands up over `RISE_SECONDS`, and that is
    // the one transition worth blending rather than cutting: it is the first
    // thing she does after the player frees her.
    if (holly.phase === 'released' && clocks) {
      HOLLY_CLIPS.trapped.sample((this.#renderTime / HOLLY_CLIPS.trapped.duration) % 1, this.#poseB);
      blendHollyPose(this.#poseB, this.#pose, clocks.riseProgress, this.#pose);
    }
    this.rig.apply(this.#pose);
  }
}
